import { formatDistanceToNow } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { ReactNode } from 'react';
import { Box, Icon, Loader, Text } from '../../../';

import { ReactComponent as ArrowTopRightSvg } from '@frontend/icons/arrow-top-right-rec.svg';
import { ReactComponent as CheckCircleFilledSvg } from '@frontend/icons/check-circle-filled.svg';

import { Transaction } from '../TransactionDropdown';
import styles from './TransactionDropdownItem.module.css';

const GroupLabel = ({ children }: { children: ReactNode }) => {
  return (
    <Text size="miniscule-callout" weight="600" color="neutral-300">
      {children}
    </Text>
  );
};

const Value = ({
  children,
  truncated,
  'data-test-id': dataTestId,
}: {
  children: ReactNode;
  truncated?: boolean;
  'data-test-id'?: string;
}) => {
  return (
    <Text
      size="tiny"
      color="neutral-500"
      mt="4"
      truncated={truncated}
      style={{ whiteSpace: 'nowrap' }}
      data-test-id={dataTestId}
    >
      {children}
    </Text>
  );
};

const TransactionStatus = ({ status }: { status: Transaction['status'] }) => {
  const color = status === 'confirming' ? 'royal-500' : 'alien-500';

  return (
    <div className={styles['confirm-status']} data-test-id="transaction-status">
      {status === 'confirming' ? (
        <Loader size={16} color={color} />
      ) : (
        <Icon component={<CheckCircleFilledSvg />} color={color} size={16} />
      )}
      <Text size="tiny-callout" weight="600" color={color}>
        {status === 'confirming' ? 'Confirming' : 'Confirmed'}
      </Text>
    </div>
  );
};

const DateElement = ({
  transactionStatus,
  date,
}: {
  date: string;
  transactionStatus: Transaction['status'];
}) => {
  const customLocale = {
    ...enUS,
    formatDistance: (
      token: string,
      count: number,
      options?: { addSuffix?: boolean; comparison?: number }
    ) => {
      if (token === 'lessThanXMinutes' && count === 1) {
        return 'just now';
      }

      return enUS?.formatDistance?.(token, count, options);
    },
  };

  const formattedDate = formatDistanceToNow(Date.parse(date), {
    addSuffix: true,
    locale: customLocale,
  });

  return (
    <Text
      className={styles['time-info']}
      size="tiny"
      color="neutral-500"
      decoration="none"
    >
      {`${
        transactionStatus === 'confirming' ? 'Detected ' : ''
      }${formattedDate}`}
      <Icon
        className={styles['icon']}
        size={16}
        color="neutral-500"
        component={<ArrowTopRightSvg />}
      />
    </Text>
  );
};

export default function TransactionDropdownItem({
  hash,
  value,
  date,
  link,
  status,
}: Transaction): JSX.Element {
  return (
    <Box
      className={styles['base']}
      as="a"
      href={link}
      target="_blank"
      rel="noreferrer"
      data-test-id="transaction-item"
    >
      <div className={styles['inner']}>
        <div className={styles['header']}>
          {status && <TransactionStatus status={status} />}
          {date && <DateElement transactionStatus={status} date={date} />}
        </div>
        <div className={styles['tx-info-row']}>
          {value && (
            <div className={styles['group']}>
              <GroupLabel>Value</GroupLabel>
              <Value data-test-id="transaction-value">{value}</Value>
            </div>
          )}
          <div className={styles['group']} style={{ flexShrink: 1 }}>
            <GroupLabel>Tx Hash</GroupLabel>
            <Value truncated data-test-id="transaction-hash">
              {hash}
            </Value>
          </div>
        </div>
      </div>
    </Box>
  );
}
